import React, { useRef } from "react";
import ReactHtmlParser from "react-html-parser";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { getLayout } from "../../store/settings/selectors";
import { getTranslations, getLocale } from "../../store/localization/selectors";
import { toggleFilters } from "../../store/settings/actions";
import { LAYOUT_XS, LAYOUT_SM, LAYOUT_MD } from "../../settings";

import Logo from "../../components/App/Logo";
import Container from "../../components/App/Container";
import Spacer from "../../components/App/Spacer";
import CompactMenuIcon from "../../components/App/CompactMenuIcon";
import Button from "../../components/App/Button";
import { login } from "../../store/auth/actions";

import Benefit1Img from "./assets/01-benefit-design-premium-pass.jpg";
import Benefit2Img from "./assets/02-benefit-free-entries.jpg";
import Benefit3Img from "./assets/03-benefit-marmomac-academy.jpg";
import Benefit4Img from "./assets/04-benefit-private-lounge.jpg";
import Benefit5Img from "./assets/05-benefit-social-content.jpg";
import Benefit6Img from "./assets/06-benefit-newsletter.jpg";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: 100%;
  height: calc(100vh - 50px);
  height: calc(calc(var(--vh, 1vh) * 100) - 50px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const MainContainer = styled.div`
  margin-top: 3px;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: auto;
`;

const CompactMenuIconWrapper = styled.div`
  position: absolute;
  top: 5px;
  right: 15px;
  width: 46px;
  height: 46px;
  flex-grow: 0;
  flex-shrink: 0;
  margin-left: 7px;
  z-index: 9999;
`;

const MacroRow = styled.div`
  display: flex;
  flex-direction: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM
      ? "column"
      : "row"};
  flex-wrap: wrap;
  gap: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? 20 : 30}px;
`;

const MacroCol = styled.div`
  width: ${(props) =>
    props.size && props.layout !== LAYOUT_XS && props.layout !== LAYOUT_SM
      ? props.size
      : 100}%;
  flex: ${(props) =>
    props.size && props.layout !== LAYOUT_XS && props.layout !== LAYOUT_SM
      ? `0 0 calc(${props.size}% - 15px)`
      : "1 1 100%"};
`;

const HeroSection = styled.div`
  background-color: #e5087e;
  color: #ffffff;
  border-radius: 12px;
  padding: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM
      ? "30px 20px"
      : "50px 40px"};
  text-align: center;
  margin-bottom: 34px;
`;

const HeroPlus = styled.div`
  font-weight: 300;
  font-size: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? "60px" : "80px"};
  line-height: 1;
  margin-bottom: 10px;
`;

const HeroTitle = styled.h1`
  font-weight: 700;
  font-size: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? "28px" : "36px"};
  line-height: 1.2;
  margin-bottom: 20px;
  text-transform: uppercase;
`;

const HeroSubtitle = styled.h2`
  font-weight: 600;
  font-size: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? "18px" : "22px"};
  line-height: 1.3;
  margin-bottom: 15px;
`;

const HeroText = styled.p`
  font-size: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? "14px" : "16px"};
  line-height: 1.5;
  color: #ededed;
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ContentSection = styled.div`
  background-color: #fafafa;
  border-radius: 12px;
  padding: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM
      ? "25px 20px"
      : "35px 30px"};
  margin-bottom: 34px;
`;

const SectionTitleSmall = styled.h4`
  font-weight: 400;
  font-size: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? "16px" : "18px"};
  line-height: 1.3;
  color: #e5087e;
  margin: 0 0 0 0;
`;

const SectionTitle = styled.h3`
  font-weight: 700;
  font-size: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? "20px" : "24px"};
  line-height: 1.3;
  color: #000000;
  margin: 12px 6px 12px 0;
  display: inline-block;
`;
const SectionTitlePrimary = styled.h3`
  font-weight: 700;
  font-size: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM ? "20px" : "24px"};
  line-height: 1.3;
  color: #e5087e;
  margin: 12px 0 12px 0;
  display: inline-block;
`;

const SectionText = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #333333;
  margin-bottom: 20px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const BenefitItem = styled.li`
  font-size: 14px;
  line-height: 24px;
  color: #333333;
  padding-left: 30px;
  margin-bottom: 15px;
  position: relative;

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #e5087e;
    font-weight: bold;
    font-size: 18px;
  }
`;

const CtaSection = styled.div`
  text-align: center;
  padding: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM
      ? "25px 20px"
      : "35px 30px"};
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 34px;
`;

const ImageSection = styled.div`
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 34px;

  & img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const TwoColumnSection = styled.div`
  display: flex;
  flex-direction: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM
      ? "column"
      : "row"};
  gap: 30px;
  margin-bottom: 34px;
`;

const Column = styled.div`
  flex: 1;
  background-color: #fafafa;
  border-radius: 12px;
  padding: ${(props) =>
    props.layout === LAYOUT_XS || props.layout === LAYOUT_SM
      ? "25px 20px"
      : "35px 30px"};
`;

const ColumnImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 20px;
`;

export default function StudiArchitettura() {
  const dispatch = useDispatch();
  dispatch(toggleFilters(false));

  const iframeRef = useRef(null);
  const onClick = () => {
    if (iframeRef.current) {
      iframeRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const layout = useSelector(getLayout);
  const translations = useSelector(getTranslations);
  const lang = useSelector(getLocale);

  let vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty("--vh", `${vh}px`);
  window.addEventListener("resize", () => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  });

  return (
    <Wrapper>
      {layout !== LAYOUT_XS && layout !== LAYOUT_SM ? (
        <Spacer size={{ value: 34, unit: "px" }} />
      ) : (
        <>
          <Spacer size={{ value: 34, unit: "px" }} />
          <CompactMenuIconWrapper>
            <CompactMenuIcon />
          </CompactMenuIconWrapper>
        </>
      )}
      <MainContainer>
        <Container>
          <center>
            {layout === LAYOUT_XS || layout === LAYOUT_SM ? (
              <Spacer size={{ value: 34, unit: "px" }} />
            ) : null}
            {layout !== LAYOUT_XS && layout !== LAYOUT_SM ? (
              <Logo height={{ value: 56, unit: "px" }} variant="plus" />
            ) : (
              <Logo width={{ value: 70, unit: "vw" }} variant="plus" />
            )}
            <Spacer size={{ value: 34, unit: "px" }} />
          </center>

          {/* Hero Section */}
          <HeroSection layout={layout}>
            {/* <HeroPlus layout={layout}>+</HeroPlus> */}
            <HeroTitle layout={layout}>
              {translations["UI"]["_studi-architettura-title"]}
            </HeroTitle>
            <HeroSubtitle layout={layout}>
              {translations["UI"]["_studi-architettura-subtitle"]}
            </HeroSubtitle>
            <HeroText layout={layout}>
              {translations["UI"]["_studi-architettura-hero-text"]}
            </HeroText>
            <Button size={"default"} variant={"pink-o"} display={"inline-block"} onClick={onClick}>{translations["UI"]["_studi-architettura-cta-title"]}
            </Button>
          </HeroSection>

          {/* Perché registrarsi */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <SectionTitle layout={layout}>
              {translations["UI"]["_studi-architettura-benefits-title"]}
            </SectionTitle>
            <SectionTitlePrimary layout={layout}>
              {translations["UI"]["_studi-architettura-benefits-title-benefit"]}
            </SectionTitlePrimary>
          </div>
          {/* <ContentSection layout={layout}>
            <BenefitsList>
              <BenefitItem>
                {translations["UI"]["_studi-architettura-benefit-1"] || "Accesso esclusivo alle ultime innovazioni nel settore della pietra naturale"}
              </BenefitItem>
              <BenefitItem>
                {translations["UI"]["_studi-architettura-benefit-2"] || "Network con i principali produttori e fornitori internazionali"}
              </BenefitItem>
              <BenefitItem>
                {translations["UI"]["_studi-architettura-benefit-3"] || "Contenuti dedicati e case study di progetti realizzati"}
              </BenefitItem>
              <BenefitItem>
                {translations["UI"]["_studi-architettura-benefit-4"] || "Aggiornamenti su eventi, workshop e seminari formativi"}
              </BenefitItem>
              <BenefitItem>
                {translations["UI"]["_studi-architettura-benefit-5"] || "Materiali tecnici e cataloghi digitali sempre disponibili"}
              </BenefitItem>
            </BenefitsList>
          </ContentSection> */}
          <TwoColumnSection layout={layout}>
            <Column layout={layout}>
              <ColumnImage src={translations["UI"]["_studi-architettura-marmomac-benefit-image-1"] || Benefit1Img} alt="" />
              <SectionTitle layout={layout}>
                {
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-title-1"
                  ]
                }
              </SectionTitle>
              <SectionText>
                {ReactHtmlParser(
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-text-1"
                  ]
                )}
              </SectionText>
            </Column>
            <Column layout={layout}>
              <ColumnImage src={translations["UI"]["_studi-architettura-marmomac-benefit-image-2"] || Benefit2Img} alt="" />
              <SectionTitle layout={layout}>
                {
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-title-2"
                  ]
                }
              </SectionTitle>
              <SectionText>
                {ReactHtmlParser(
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-text-2"
                  ]
                )}
              </SectionText>
            </Column>
          </TwoColumnSection>

          {/* Marmomac 2026 */}
          <TwoColumnSection layout={layout}>
            <Column layout={layout}>
              <ColumnImage src={translations["UI"]["_studi-architettura-marmomac-benefit-image-3"] || Benefit3Img} alt="" />
              <SectionTitle layout={layout}>
                {
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-title-3"
                  ]
                }
              </SectionTitle>
              <SectionText>
                {ReactHtmlParser(
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-text-3"
                  ]
                )}
              </SectionText>
            </Column>
            <Column layout={layout}>
              <ColumnImage src={translations["UI"]["_studi-architettura-marmomac-benefit-image-4"] || Benefit4Img} alt="" />
              <SectionTitle layout={layout}>
                {
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-title-4"
                  ]
                }
              </SectionTitle>
              <SectionText>
                {ReactHtmlParser(
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-text-4"
                  ]
                )}
              </SectionText>
            </Column>
          </TwoColumnSection>

          <TwoColumnSection layout={layout}>
            <Column layout={layout}>
              <ColumnImage src={translations["UI"]["_studi-architettura-marmomac-benefit-image-5"] || Benefit5Img} alt="" />
              <SectionTitle layout={layout}>
                {
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-title-5"
                  ]
                }
              </SectionTitle>
              <SectionText>
                {ReactHtmlParser(
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-text-5"
                  ]
                )}
              </SectionText>
            </Column>
            <Column layout={layout}>
              <ColumnImage src={translations["UI"]["_studi-architettura-marmomac-benefit-image-6"] || Benefit6Img} alt="" />
              <SectionTitle layout={layout}>
                {
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-title-6"
                  ]
                }
              </SectionTitle>
              <SectionText>
                {ReactHtmlParser(
                  translations["UI"][
                    "_studi-architettura-marmomac-benefit-text-6"
                  ]
                )}
              </SectionText>
            </Column>
          </TwoColumnSection>

          {/* Call to Action finale */}
          <CtaSection layout={layout}>
            <SectionTitle layout={layout} style={{ marginBottom: "15px" }}>
              {translations["UI"]["_studi-architettura-cta-title"]}
            </SectionTitle>
            <SectionText>
              {translations["UI"]["_studi-architettura-cta-text"]}
            </SectionText>
            <div style={{ marginTop: "30px", width: "100%", display: "flex", justifyContent: "center" }}>
              <iframe
                ref={iframeRef} 
                src={lang === "it" ? "https://ers.veronafiere.it/Prequalifiche/Form?idman=MMP26&lang=IT&token=S6QHbPzljC9NoHYPttQa2HLsfDh0Yxai" : "https://ers.veronafiere.it/Prequalifiche/Form?idman=MMP26&lang=EN&token=S6QHbPzljC9NoHYPttQa2HLsfDh0Yxai"}
                title={translations["UI"]["_studi-architettura-cta"]}
                width="100%"
                height="700"
                style={{ border: "none", borderRadius: "12px", maxWidth: "700px" }}
                allowFullScreen
              />
            </div>
          </CtaSection>

          <Spacer size={{ value: 34, unit: "px" }} />
        </Container>
      </MainContainer>
    </Wrapper>
  );
}
